import React, { useCallback, useEffect, useRef, useState } from 'react';

import { App, Button, Form, Input, Modal, Select, Switch, Typography } from 'antd';

import { constants } from 'helpers/constants';
import { functions } from 'helpers/functions';
import QueryString from 'query-string';
import Truncate from 'v2/components/Shared/TruncateText';
import { useListConnectOptions } from 'v2/hooks/useListConnectOptions';
import { useListTriggers } from 'v2/hooks/useListTriggers';
import { useUpdateConnectAction } from 'v2/hooks/useUpdateConnectAction';
import { connectService } from 'v2/services/connect';
import { run } from 'validations/ruleRunner';

import Validations from './validations';
import { useActionState } from '../../contexts/ActionStateContext';
import { useConnectState } from '../../contexts/ConnectStateContext';
import { getOptionParametersQuery, getOptionsParametersShouldModify } from '../../helpers/actionOptionsHelpers';

const EditAction: React.FC = () => {
  const { message } = App.useApp();
  const { currentConnect } = useConnectState();
  const {
    currentAction,
    modalEditAction: modal,
    optionsAction,
    setCurrentAction,
    closeEditActionModal,
    addOptionAction,
    resetOptionAction
  } = useActionState();
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);

  const { triggers } = useListTriggers();
  const { options } = useListConnectOptions(currentConnect?.autoresponder_id);
  const { updateConnectAction, isUpdating } = useUpdateConnectAction();

  const prevCurrentActionRef = useRef<any>(currentAction);

  const errorFor = useCallback(
    (field: string) => (showErrors ? validationErrors[field] || '' : ''),
    [showErrors, validationErrors]
  );
  const hasError = useCallback(
    (field: string) => showErrors && !!validationErrors[field],
    [showErrors, validationErrors]
  );

  const handleChange = useCallback(
    (field: any, value: any) => {
      const newAction = functions.cloneDeep(currentAction);

      if (field === 'option_id') {
        const selectedOption = options && Array.isArray(options) && options.find((item: any) => item.id === value);
        const fields: Record<string, any> = {};

        if (selectedOption && selectedOption.parameters && Array.isArray(selectedOption.parameters.campos)) {
          selectedOption.parameters.campos.forEach((item: any) => {
            fields[item.nome] = newAction.options_parameters[item.nome];
          });
        }

        newAction.fields = fields;
        newAction.options_parameters = fields;
      }

      newAction[field] = value;
      setCurrentAction(functions.cloneDeep(newAction));
    },
    [setCurrentAction, currentAction, options]
  );

  const handleChangeField = useCallback(
    (field: any, value: any) => {
      const newAction = functions.cloneDeep(currentAction);

      newAction.fields = newAction.fields || {};

      if (newAction && newAction.options_parameters) {
        newAction.options_parameters[field] = String(value);
      }

      newAction.fields = newAction.options_parameters;

      setCurrentAction(functions.cloneDeep(newAction));
    },
    [setCurrentAction, currentAction]
  );

  const dispatchNewOptionParam = useCallback(
    async (autoResponderId: number, connectId: number, itemName: string, params: any) => {
      const keys = itemName.split('_');
      const toSearch = `${keys[0]}_${keys[1]}`;

      let paramsString = undefined;

      if (params && typeof params === 'object') {
        paramsString = QueryString.stringify(params);
      }

      const result = await connectService.getActionFields(autoResponderId, connectId, toSearch, paramsString);
      const fieldData = result?.data ?? result;

      addOptionAction(itemName, functions.cloneDeep(fieldData));
    },
    [addOptionAction]
  );

  const fillOptionsParametersList = useCallback(
    (optionsParametersShouldModify: string[]) => {
      const selectedOption =
        options && Array.isArray(options) && options.find((item: any) => item.id === currentAction.option_id);

      if (!optionsParametersShouldModify || optionsParametersShouldModify.length === 0) {
        return;
      }

      if (!selectedOption || !selectedOption.parameters || !Array.isArray(selectedOption.parameters.campos)) {
        return;
      }

      Promise.all(
        selectedOption.parameters.campos.map(async (item: any) => {
          if (!optionsParametersShouldModify.includes(item.nome)) {
            return;
          }

          if (item.campo_carregar) {
            const params = getOptionParametersQuery(item, selectedOption, currentAction);
            await dispatchNewOptionParam(currentConnect.autoresponder_id, currentConnect.id, item.nome, params);
            return;
          }

          await dispatchNewOptionParam(currentConnect.autoresponder_id, currentConnect.id, item.nome, undefined);
        })
      );
    },
    [options, currentAction, currentConnect, dispatchNewOptionParam]
  );

  const closeModal = useCallback(() => {
    setShowErrors(false);
    closeEditActionModal();
  }, [closeEditActionModal]);

  const canSave =
    !!currentAction.description?.trim() && !!currentAction.trigger_id && !!currentAction.option_id && !isUpdating;

  const saveAction = useCallback(async () => {
    const errors = run(currentAction, Validations) as Record<string, string>;
    setValidationErrors(errors);

    if (
      errors?.description ||
      !currentAction.description?.trim() ||
      !currentAction.trigger_id ||
      !currentAction.option_id
    ) {
      setShowErrors(true);
      message.warning('Preencha todos os campos obrigatórios.');
      return;
    }

    try {
      await updateConnectAction({ connectId: currentConnect.id, action: currentAction });
      closeModal();
      message.success('Ação salva com sucesso!');
    } catch (error: any) {
      message.error(error?.message ?? 'Erro ao salvar ação');
    }
  }, [updateConnectAction, currentConnect, currentAction, closeModal, message]);

  const getOptionsFieldName = useCallback((action: any, itemName: string): [string, any] => {
    if (itemName?.includes('_field_')) {
      itemName = itemName.replace('_field', '');
    }

    let value = action.options_parameters[itemName];

    if (action.fields) {
      value = action.fields[itemName];
    }

    return [itemName, value];
  }, []);

  useEffect(() => {
    const prevAction = prevCurrentActionRef.current;

    if (currentAction !== prevAction) {
      setValidationErrors(run(currentAction, Validations) as Record<string, string>);
    }

    const actionOptionsHasUpdated =
      prevAction.options_parameters !== currentAction.options_parameters ||
      prevAction.option_id !== currentAction.option_id;
    const shouldUpdateOptionsParametersList =
      currentAction && currentAction.options_parameters && currentConnect && actionOptionsHasUpdated;

    const optionsParametersShouldModify = getOptionsParametersShouldModify(currentAction, prevAction, options);

    optionsParametersShouldModify.forEach((item: string) => {
      resetOptionAction(item);
    });

    if (shouldUpdateOptionsParametersList) {
      fillOptionsParametersList(optionsParametersShouldModify);
    }

    prevCurrentActionRef.current = currentAction;
  }, [currentAction, currentConnect, options, resetOptionAction, fillOptionsParametersList]);

  useEffect(() => {
    if (!modal) {
      setShowErrors(false);
    }
  }, [modal]);

  const currentOptions = Array.isArray(options) ? options : [];
  const selectedOption = currentOptions.find((item: any) => item.id === currentAction.option_id);

  return (
    <Modal
      open={modal}
      onCancel={closeModal}
      width={680}
      destroyOnClose
      title='Editar ação'
      footer={
        <div className='flex flex-wrap items-center justify-end gap-2'>
          <Button type='text' onClick={closeModal} disabled={isUpdating}>
            Cancelar
          </Button>
          <Button type='primary' disabled={!canSave} loading={isUpdating} onClick={saveAction}>
            Salvar alterações
          </Button>
        </div>
      }
    >
      <Form layout='vertical' className='flex flex-col gap-5'>
        {currentConnect && (
          <div className='flex items-center gap-3 rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
            {currentConnect.image && (
              <img
                src={currentConnect.image}
                alt={currentConnect?.name || 'Connect'}
                className='h-12 max-w-24 object-contain'
              />
            )}
            <div className='min-w-0'>
              <Typography.Text strong className='block'>
                {currentConnect?.name}
              </Typography.Text>
              <Typography.Text type='secondary' className='block truncate text-sm'>
                {currentConnect?.description}
              </Typography.Text>
              {currentConnect?.parameters?.[0] && (
                <>
                  <Typography.Text strong className='mt-1 block text-sm'>
                    {currentConnect.parameters[0].label}
                  </Typography.Text>
                  <Typography.Text type='secondary' className='block text-sm'>
                    <Truncate count={50} final='...'>
                      {currentConnect.parameters[0].value}
                    </Truncate>
                  </Typography.Text>
                </>
              )}
            </div>
          </div>
        )}

        <div className='flex items-center justify-between rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) px-4 py-3'>
          <div className='flex flex-col'>
            <Typography.Text strong>Status da ação</Typography.Text>
            <Typography.Text type='secondary' className='text-sm'>
              {currentAction.active ? 'Esta ação está ativa' : 'Esta ação está inativa'}
            </Typography.Text>
          </div>
          <div className='flex items-center gap-2'>
            <Typography.Text type='secondary'>{currentAction.active ? 'Ativada' : 'Desativada'}</Typography.Text>
            <Switch checked={currentAction.active} onChange={checked => handleChange('active', checked)} />
          </div>
        </div>

        <Form.Item
          label='Título da ação'
          required
          validateStatus={hasError('description') ? 'error' : undefined}
          help={hasError('description') ? errorFor('description') : 'Identifique esta ação para encontrá-la depois'}
          className='mb-0'
        >
          <Input
            placeholder='Ex.: Enviar lead para lista de compradores'
            value={currentAction.description}
            onChange={e => handleChange('description', e.target.value)}
            maxLength={120}
            showCount
          />
        </Form.Item>

        <div className='flex flex-col gap-3'>
          <div>
            <Typography.Text strong className='block'>
              Quando isso acontecer
            </Typography.Text>
            <Typography.Text type='secondary'>Escolha o gatilho que dispara esta ação</Typography.Text>
          </div>
          <Form.Item
            label='Gatilho'
            required
            validateStatus={hasError('trigger_id') ? 'error' : undefined}
            help={hasError('trigger_id') ? errorFor('trigger_id') : undefined}
            className='mb-0'
          >
            <Select
              className='w-full'
              placeholder='Selecione um gatilho'
              value={currentAction.trigger_id}
              onChange={value => handleChange('trigger_id', value)}
              options={
                triggers && Array.isArray(triggers)
                  ? triggers.map((item: any) => ({ value: item.id, label: item.name }))
                  : []
              }
            />
          </Form.Item>
        </div>

        <div className='flex flex-col gap-3'>
          <div>
            <Typography.Text strong className='block'>
              Faça isso
            </Typography.Text>
            <Typography.Text type='secondary'>Defina a ação executada no provedor</Typography.Text>
          </div>
          <Form.Item
            label='Ação'
            required
            validateStatus={hasError('option_id') ? 'error' : undefined}
            help={hasError('option_id') ? errorFor('option_id') : undefined}
            className='mb-0'
          >
            <Select
              className='w-full'
              placeholder='Selecione uma ação'
              value={currentAction.option_id}
              onChange={value => handleChange('option_id', value)}
              disabled={!(options && Array.isArray(options))}
              options={currentOptions.map((option: any) => ({ value: option.id, label: option.title }))}
            />
          </Form.Item>
        </div>

        {currentAction.trigger_id &&
          currentAction.option_id &&
          currentAction.options_parameters &&
          selectedOption &&
          selectedOption.parameters &&
          Array.isArray(selectedOption.parameters.campos) &&
          selectedOption.parameters.campos.map((item: any) => {
            const [itemName, itemValue] = getOptionsFieldName(currentAction, item.nome);
            return item.tipo === 'list' || item.tipo === 'listdinamico' ? (
              <Form.Item
                key={`fieldAction-${item.nome}`}
                label={item.label}
                className='mb-0'
                required
                extra={
                  <>
                    {item.tooltip && <Typography.Text type='secondary'>{item.tooltip}</Typography.Text>}
                    {currentConnect?.autoresponder_id === constants.MAUTIC_AUTORESPONDER_ID && (
                      <Typography.Text type='secondary' className='mt-1 block text-xs'>
                        * Caso a listagem não esteja carregando, limpe o cache do seu Mautic ou procure ajuda na{' '}
                        <Typography.Link href='https://mautic.org/support' rel='noopener noreferrer' target='_blank'>
                          página de suporte do Mautic
                        </Typography.Link>
                        .
                      </Typography.Text>
                    )}
                  </>
                }
              >
                <Select
                  className='w-full'
                  value={String(currentAction.options_parameters[item.nome])}
                  onChange={value => handleChangeField(item.nome, value)}
                  disabled={
                    !currentAction.options_parameters ||
                    !Array.isArray(optionsAction[item.nome]) ||
                    optionsAction[item.nome].length <= 0
                  }
                  options={
                    optionsAction[item.nome] &&
                    Array.isArray(optionsAction[item.nome]) &&
                    optionsAction[item.nome].length > 0
                      ? optionsAction[item.nome].map((opt: any) => ({ value: String(opt.id), label: opt.name }))
                      : []
                  }
                />
              </Form.Item>
            ) : (
              <Form.Item key={`fieldAction-${item.nome}`} label={item.label} className='mb-0' required>
                <Input
                  value={itemValue}
                  onChange={e => handleChangeField(itemName, e.target.value)}
                  placeholder={item.label}
                />
              </Form.Item>
            );
          })}
      </Form>
    </Modal>
  );
};

export default EditAction;
